import React from "react";
import Header from "./Header";
import Footer from "./Footer";

// Section content. Block types: p, h3, callout, ul, ol, dl
const SECTIONS = [
 {
  "id": 1,
  "title": "Purpose",
  "blocks": [
   [
    "p",
    "Beeppee provides technology that can help users locate and monitor persons, vehicles, assets and other items."
   ],
   [
    "p",
    "Because tracking technology can affect privacy, safety and personal autonomy, this Policy establishes rules for responsible and lawful use of Beeppee tracking functionality."
   ],
   [
    "p",
    "This Policy supplements the:"
   ],
   [
    "ul",
    [
     "Beeppee Terms & Conditions; and",
     "Beeppee Privacy Policy."
    ]
   ],
   [
    "p",
    "If there is a conflict, the provision providing the greater protection required by applicable law will apply."
   ]
  ]
 },
 {
  "id": 2,
  "title": "Core Principle",
  "blocks": [
   [
    "callout",
    "A Beeppee user may only track or monitor another person where the user has lawful authority, an appropriate legal basis, or the required consent or authorization."
   ],
   [
    "p",
    "The technical ability to track someone does not mean that the tracking is legally permitted."
   ],
   [
    "p",
    "Beeppee must not be used as a tool for stalking, harassment, coercive control, unauthorized surveillance or unlawful monitoring."
   ]
  ]
 },
 {
  "id": 3,
  "title": "Definitions",
  "blocks": [
   [
    "dl",
    [
     [
      "Tracker",
      "the Beeppee user initiating or controlling tracking."
     ],
     [
      "Tracked Person",
      "a person whose location or movement is being monitored."
     ],
     [
      "Tracked Asset",
      "a vehicle, item, device or other property being monitored."
     ],
     [
      "Consent",
      "a freely given, specific, informed and appropriately obtained agreement where consent is the applicable legal basis."
     ],
     [
      "Tracking Relationship",
      "a relationship created within Beeppee that permits one user or authorized organization to receive location or monitoring information concerning another person, vehicle or asset."
     ]
    ]
   ]
  ]
 },
 {
  "id": 4,
  "title": "Person Tracking",
  "blocks": [
   [
    "h3",
    "4.1 General Rule"
   ],
   [
    "p",
    "You must not track another person secretly or without appropriate authorization."
   ],
   [
    "p",
    "Where consent is required, the Tracked Person should receive sufficient information to understand:"
   ],
   [
    "ul",
    [
     "that tracking is occurring;",
     "who is conducting the tracking;",
     "what information is being collected;",
     "why tracking is taking place;",
     "who may receive the information; and",
     "how tracking may be stopped or withdrawn where applicable."
    ]
   ],
   [
    "h3",
    "4.2 Family and Household Tracking"
   ],
   [
    "p",
    "Beeppee may be used for legitimate family safety purposes."
   ],
   [
    "p",
    "However, being a:"
   ],
   [
    "ul",
    [
     "spouse;",
     "partner;",
     "parent;",
     "sibling;",
     "relative; or",
     "household member"
    ]
   ],
   [
    "p",
    "does not automatically give a person unlimited authority to track another adult."
   ],
   [
    "p",
    "Adult users should ordinarily know about and authorize tracking where consent is the applicable legal basis."
   ]
  ]
 },
 {
  "id": 5,
  "title": "Children and Minors",
  "blocks": [
   [
    "h3",
    "5.1 Minimum Age"
   ],
   [
    "p",
    "Beeppee permits users aged 10 years and above, subject to applicable law."
   ],
   [
    "h3",
    "5.2 Parent or Guardian"
   ],
   [
    "p",
    "A parent or legal guardian may use Beeppee to support the safety and supervision of a child where legally permitted."
   ],
   [
    "p",
    "Where processing relies on consent and applicable law requires parental or guardian consent, the required consent must be obtained."
   ],
   [
    "h3",
    "5.3 Minor's Privacy"
   ],
   [
    "p",
    "Parents and guardians should use tracking proportionately and for legitimate safety or supervision purposes."
   ],
   [
    "p",
    "Tracking must not be used to:"
   ],
   [
    "ul",
    [
     "abuse;",
     "threaten;",
     "humiliate;",
     "harass; or",
     "unlawfully control"
    ]
   ],
   [
    "p",
    "a child."
   ],
   [
    "h3",
    "5.4 Teenagers"
   ],
   [
    "p",
    "As children become older and more capable of making decisions concerning their privacy, users and guardians should consider age, maturity and the circumstances when configuring tracking."
   ],
   [
    "p",
    "Beeppee may implement additional controls for minors as its products develop."
   ],
   [
    "h3",
    "5.5 Verification"
   ],
   [
    "p",
    "Beeppee may use reasonable age and consent verification mechanisms where required."
   ]
  ]
 },
 {
  "id": 6,
  "title": "Employee and Workforce Tracking",
  "blocks": [
   [
    "h3",
    "6.1 Business Responsibility"
   ],
   [
    "p",
    "An employer or organization using Beeppee to track employees, drivers, contractors or other personnel is responsible for ensuring that its use complies with applicable:"
   ],
   [
    "ul",
    [
     "privacy law;",
     "employment law;",
     "contractual obligations;",
     "workplace policies; and",
     "data-protection requirements."
    ]
   ],
   [
    "h3",
    "6.2 Transparency"
   ],
   [
    "p",
    "Organizations should provide appropriate notice explaining:"
   ],
   [
    "ul",
    [
     "what is being tracked;",
     "when tracking occurs;",
     "why it occurs;",
     "who can access the information;",
     "how long information is retained; and",
     "how employees may raise concerns."
    ]
   ],
   [
    "h3",
    "6.3 Proportionality"
   ],
   [
    "p",
    "Organizations should configure tracking proportionately to the legitimate purpose."
   ],
   [
    "p",
    "For example, where continuous location tracking is not necessary, an organization should consider whether event-based, journey-based or working-hours tracking is sufficient."
   ],
   [
    "h3",
    "6.4 Outside Working Hours"
   ],
   [
    "p",
    "Employers should not use Beeppee to monitor an employee outside authorized working periods unless they have an appropriate legal basis and are permitted to do so under applicable law."
   ],
   [
    "h3",
    "6.5 Fleet Drivers"
   ],
   [
    "p",
    "Fleet operators may track company vehicles and drivers for legitimate operational purposes."
   ],
   [
    "p",
    "Drivers should be informed about applicable tracking arrangements."
   ]
  ]
 },
 {
  "id": 7,
  "title": "Vehicle Tracking",
  "blocks": [
   [
    "h3",
    "7.1 Ownership or Authority"
   ],
   [
    "p",
    "A user may track a vehicle where they:"
   ],
   [
    "ul",
    [
     "own it;",
     "lease it with authority to install tracking;",
     "operate a lawful fleet-management program; or",
     "otherwise have legal authority."
    ]
   ],
   [
    "h3",
    "7.2 Driver Privacy"
   ],
   [
    "p",
    "Where a vehicle is assigned to an individual driver, the vehicle owner's authority to track the vehicle does not necessarily eliminate privacy obligations relating to the driver."
   ],
   [
    "p",
    "Appropriate notice should be provided where required."
   ],
   [
    "h3",
    "7.3 Personal Vehicles"
   ],
   [
    "p",
    "A person should not install or activate a tracker on another person's vehicle without appropriate authorization."
   ]
  ]
 },
 {
  "id": 8,
  "title": "Asset and Item Tracking",
  "blocks": [
   [
    "p",
    "Users may track items or assets they own or are authorized to monitor."
   ],
   [
    "p",
    "Examples may include:"
   ],
   [
    "ul",
    [
     "luggage;",
     "equipment;",
     "business assets;",
     "packages;",
     "fleet assets; and",
     "other property."
    ]
   ],
   [
    "p",
    "Tracking an item must not be used as a pretext for unlawfully tracking a person."
   ]
  ]
 },
 {
  "id": 9,
  "title": "Geofencing",
  "blocks": [
   [
    "p",
    "Users may create Geofences around:"
   ],
   [
    "ul",
    [
     "homes;",
     "workplaces;",
     "schools;",
     "warehouses;",
     "business premises;",
     "delivery areas;",
     "routes; or",
     "other locations."
    ]
   ],
   [
    "p",
    "Geofences must be used for legitimate purposes."
   ],
   [
    "p",
    "Users must not create Geofences for harassment, intimidation or unlawful surveillance."
   ]
  ]
 },
 {
  "id": 10,
  "title": "Panic Alerts",
  "blocks": [
   [
    "h3",
    "10.1 Intended Purpose"
   ],
   [
    "p",
    "Panic Alerts are intended to communicate that a user believes they require attention or assistance."
   ],
   [
    "h3",
    "10.2 Not Emergency Services"
   ],
   [
    "p",
    "A Panic Alert does not automatically create a police, ambulance, fire or other emergency response."
   ],
   [
    "h3",
    "10.3 False Alerts"
   ],
   [
    "p",
    "Users must not deliberately create false Panic Alerts to:"
   ],
   [
    "ul",
    [
     "frighten another person;",
     "harass;",
     "deceive;",
     "trigger unnecessary emergency action;",
     "retaliate against another user; or",
     "cause disruption."
    ]
   ],
   [
    "h3",
    "10.4 Sharing"
   ],
   [
    "p",
    "A Panic Alert may disclose location or other information to designated recipients depending on the user's configuration."
   ],
   [
    "p",
    "Users should therefore configure recipients carefully."
   ]
  ]
 },
 {
  "id": 11,
  "title": "Trip and Location-Sharing Links",
  "blocks": [
   [
    "h3",
    "11.1 User Responsibility"
   ],
   [
    "p",
    "A user creating a trip or location-sharing link is responsible for choosing appropriate recipients."
   ],
   [
    "h3",
    "11.2 Confidentiality"
   ],
   [
    "p",
    "Recipients must treat location and trip information as private unless they have a lawful reason to disclose it."
   ],
   [
    "h3",
    "11.3 Prohibited Sharing"
   ],
   [
    "p",
    "A user must not:"
   ],
   [
    "ul",
    [
     "post another person's private location publicly;",
     "sell access to private tracking information;",
     "forward a private link to unauthorized recipients;",
     "use a link to facilitate stalking; or",
     "share confidential information from another user's Account."
    ]
   ],
   [
    "h3",
    "11.4 Revocation"
   ],
   [
    "p",
    "Where functionality permits, users should revoke links when sharing is no longer necessary."
   ]
  ]
 },
 {
  "id": 12,
  "title": "Tracking Without Consent",
  "blocks": [
   [
    "p",
    "Beeppee may restrict or prohibit tracking where it reasonably suspects:"
   ],
   [
    "ul",
    [
     "stalking;",
     "harassment;",
     "domestic abuse;",
     "coercive control;",
     "unauthorized surveillance;",
     "impersonation;",
     "account compromise;",
     "unlawful monitoring; or",
     "other prohibited activity."
    ]
   ],
   [
    "p",
    "Where legally permitted, Beeppee may investigate and take appropriate action."
   ]
  ]
 },
 {
  "id": 13,
  "title": "Consent Management",
  "blocks": [
   [
    "p",
    "Where Beeppee provides consent-management functionality, users should be able to understand the status of a Tracking Relationship."
   ],
   [
    "p",
    "Depending on the feature, Beeppee may provide:"
   ],
   [
    "ul",
    [
     "invitations;",
     "acceptance requests;",
     "notifications;",
     "tracking status;",
     "revocation functionality; or",
     "account controls."
    ]
   ],
   [
    "p",
    "Technical implementation may vary by product."
   ]
  ]
 },
 {
  "id": 14,
  "title": "Withdrawal of Consent",
  "blocks": [
   [
    "p",
    "Where consent is the lawful basis for tracking, the person who provided consent should be able to withdraw it through available functionality or by contacting Beeppee where appropriate."
   ],
   [
    "p",
    "Withdrawal may cause the relevant tracking feature to stop operating."
   ],
   [
    "p",
    "Withdrawal does not necessarily invalidate processing lawfully carried out before withdrawal."
   ]
  ]
 },
 {
  "id": 15,
  "title": "Business and Fleet Administrators",
  "blocks": [
   [
    "p",
    "Business administrators are responsible for:"
   ],
   [
    "ul",
    [
     "assigning appropriate personnel access;",
     "protecting administrator credentials;",
     "limiting access to authorized personnel;",
     "reviewing tracking permissions;",
     "removing former employees or administrators;",
     "maintaining appropriate notices; and",
     "complying with applicable law."
    ]
   ]
  ]
 },
 {
  "id": 16,
  "title": "Access to Tracking Information",
  "blocks": [
   [
    "p",
    "Access to tracking information should be limited to people who have a legitimate reason to receive it."
   ],
   [
    "p",
    "Users must not:"
   ],
   [
    "ul",
    [
     "sell private tracking information;",
     "use tracking information for unrelated purposes;",
     "expose information publicly without authorization; or",
     "provide access to unauthorized third parties."
    ]
   ]
  ]
 },
 {
  "id": 17,
  "title": "Law Enforcement and Emergency Requests",
  "blocks": [
   [
    "p",
    "Beeppee may disclose information in response to a valid legal requirement or where disclosure is otherwise permitted by applicable law."
   ],
   [
    "p",
    "Users should not represent themselves as law enforcement or emergency personnel when using Beeppee."
   ]
  ]
 },
 {
  "id": 18,
  "title": "Security of Tracking Information",
  "blocks": [
   [
    "p",
    "Because location information can be sensitive, users must:"
   ],
   [
    "ul",
    [
     "protect Account credentials;",
     "secure Devices;",
     "avoid sharing passwords;",
     "protect private links; and",
     "promptly report suspected unauthorized access."
    ]
   ],
   [
    "p",
    "Beeppee will implement reasonable technical and organizational measures to protect information."
   ]
  ]
 },
 {
  "id": 19,
  "title": "Misuse Reporting",
  "blocks": [
   [
    "p",
    "Users who believe that someone is using Beeppee to unlawfully track or monitor them should contact: info@beeppee.com"
   ],
   [
    "p",
    "Where appropriate, Beeppee may:"
   ],
   [
    "ul",
    [
     "investigate;",
     "suspend an Account;",
     "disable a tracking relationship;",
     "restrict sharing;",
     "preserve relevant records where legally permitted or required; or",
     "take other appropriate action."
    ]
   ],
   [
    "callout",
    "If a person believes they are in immediate physical danger, they should contact the appropriate emergency or law-enforcement service rather than relying solely on Beeppee."
   ]
  ]
 },
 {
  "id": 20,
  "title": "Business Customer Responsibility",
  "blocks": [
   [
    "p",
    "A Business User remains responsible for its own decisions concerning:"
   ],
   [
    "ul",
    [
     "who is tracked;",
     "why tracking occurs;",
     "when tracking occurs;",
     "what information is collected;",
     "who receives the information; and",
     "how long information is retained."
    ]
   ],
   [
    "p",
    "Beeppee provides technology and does not independently determine whether a Business User's particular monitoring program complies with employment, privacy or other applicable law."
   ]
  ]
 },
 {
  "id": 21,
  "title": "Prohibited Uses",
  "blocks": [
   [
    "p",
    "The following are prohibited:"
   ],
   [
    "ol",
    [
     "stalking;",
     "covert tracking without lawful authorization;",
     "harassment;",
     "intimidation;",
     "coercive monitoring;",
     "unauthorized tracking of a spouse or partner;",
     "unauthorized employee surveillance;",
     "unauthorized tracking of a child;",
     "misuse of Panic Alerts;",
     "misuse of trip links;",
     "publication of private location information;",
     "unauthorized access to tracking Accounts;",
     "sale of private location information;",
     "tracking for unlawful purposes; ",
     "and any other use prohibited by applicable law."
    ]
   ]
  ]
 },
 {
  "id": 22,
  "title": "Enforcement",
  "blocks": [
   [
    "p",
    "Beeppee may take proportionate action where this Policy is violated, including:"
   ],
   [
    "ul",
    [
     "warnings;",
     "feature restrictions;",
     "suspension;",
     "termination;",
     "removal of content or links;",
     "investigation; and",
     "referral to appropriate authorities where legally required or permitted."
    ]
   ]
  ]
 },
 {
  "id": 23,
  "title": "Changes to This Policy",
  "blocks": [
   [
    "p",
    "Beeppee may update this Policy as products, laws and safety requirements evolve."
   ],
   [
    "p",
    "Material changes may be communicated through the application, website or other reasonable means."
   ]
  ]
 },
 {
  "id": 24,
  "title": "Contact",
  "blocks": [
   [
    "dl",
    [
     [
      "Registered Company",
      "Beeppoint solution limited"
     ],
     [
      "CAC Number",
      "RC:2015999"
     ],
     [
      "Registered Address",
      "[INSERT]"
     ],
     [
      "Email",
      "info@beeppee.com"
     ],
     [
      "Telephone",
      "+234 916 338 5136"
     ],
     [
      "Privacy/Data Protection Contact",
      "[INSERT]"
     ]
    ]
   ]
  ]
 }
];

const META = [
  ["Version", "1.0"],
  ["Effective Date", "13/10/2026"],
  ["Last Updated", "13/10/2026"],
];

const linkClass = "text-amber-800 underline underline-offset-2";

// Turns the email address and phone number inside a string into links.
function Linkify({ text }) {
  return text.split(/(info@beeppee\.com|\+234 916 338 5136)/).map((part, i) => {
    if (part === "info@beeppee.com")
      return <a key={i} className={linkClass} href={`mailto:${part}`}>{part}</a>;
    if (part === "+234 916 338 5136")
      return <a key={i} className={linkClass} href="tel:+2349163385136">{part}</a>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function Block({ type, value }) {
  switch (type) {
    case "p":
      return <p className="mt-3"><Linkify text={value} /></p>;
    case "h3":
      return <h3 className="mt-7 font-poppins text-lg font-semibold text-stone-900">{value}</h3>;
    case "callout":
      return (
        <p className="mt-4 rounded-r-lg border-l-4 border-amber-500 bg-amber-50 px-5 py-4 font-medium text-stone-900">
          {value}
        </p>
      );
    case "ul":
      return (
        <ul className="mt-3 list-disc space-y-1 pl-6 marker:text-amber-500">
          {value.map((item) => <li key={item}>{item}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="mt-3 list-decimal space-y-1 pl-6 marker:font-semibold marker:text-amber-700">
          {value.map((item) => <li key={item}>{item}</li>)}
        </ol>
      );
    case "dl":
      return (
        <dl className="mt-3 divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white">
          {value.map(([term, desc]) => (
            <div key={term} className="grid gap-1 px-5 py-3 sm:grid-cols-3">
              <dt className="font-semibold text-stone-900">{term}</dt>
              <dd className="sm:col-span-2"><Linkify text={desc} /></dd>
            </div>
          ))}
        </dl>
      );
    default:
      return null;
  }
}

function TocList({ className = "" }) {
  return (
    <ul className={className}>
      {SECTIONS.map(({ id, title }) => (
        <li key={id}>
          <a
            href={`#s${id}`}
            className="block rounded px-2 py-1 text-stone-600 hover:bg-amber-100 hover:text-stone-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-600"
          >
            {id}. {title}
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function TrackingConsentPolicy() {
  return (
    <>
        <Header/> 
            <div className="scroll-smooth bg-stone-50 font-poppins text-lg leading-8 text-stone-700 antialiased pt-16">
            <header className="border-b border-amber-300 bg-amber-100">
                <div className="mx-auto max-w-6xl px-6 py-12">
                <h1 className="font-poppins text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">
                    Beeppee Tracking &amp; Consent Policy
                </h1>
                <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-1 font-poppins text-sm font-medium text-stone-700">
                    {META.map(([label, value]) => (
                    <div key={label} className="flex gap-2">
                        <dt>{label}:</dt>
                        <dd>{value}</dd>
                    </div>
                    ))}
                </dl>
                </div>
            </header>

            <div className="mx-auto max-w-6xl gap-12 px-6 py-10 lg:grid lg:grid-cols-[16rem_1fr]">
                <nav
                aria-label="Table of contents"
                className="mb-10 font-poppins text-sm lg:sticky lg:top-6 lg:mb-0 lg:max-h-[calc(100vh-3rem)] lg:self-start lg:overflow-y-auto"
                >
                <details className="rounded-lg border border-stone-200 bg-white p-3 lg:hidden">
                    <summary className="cursor-pointer font-semibold text-stone-900">Contents</summary>
                    <TocList className="mt-2" />
                </details>
                <TocList className="hidden lg:block" />
                </nav>

                <main className="max-w-3xl">
                {SECTIONS.map(({ id, title, blocks }) => (
                    <section
                    key={id}
                    id={`s${id}`}
                    className="scroll-mt-6 border-t border-stone-200 py-8 first:border-0">
                    <h2 className="font-poppins text-2xl font-bold text-stone-900">{id}. {title}</h2>
                    {blocks.map(([type, value], i) => <Block key={i} type={type} value={value} />)}
                    </section>
                ))}
                </main>
            </div>
            </div>
        <Footer/>
     </>
  );
}